import { Link } from "react-router-dom";
import { academyProgram, images, companyDetails } from "../data";
import { getOptimizedImageUrl } from "../lib/utils";
import { UpcomingCoursesSection } from "./UpcomingCourses";
import SEO from "../components/SEO";

export default function Courses() {
  // Participant testimonials specifically for the leadership journey
  const courseTestimonials = [
    {
      quote: "Jeg fik ikke bare nye værktøjer – jeg fik et nyt perspektiv på mit lederskab. Forløbet gav mig ro, retning og mod til at træffe de beslutninger, jeg længe havde udskudt. Jeg blev mere bevidst om mine værdier og min rolle som leder.",
      author: "Leder i detailbranchen",
      stars: 5
    },
    {
      quote: "Jeg gik fra at reagere til at lede. Det mest værdifulde var tiden mellem modulerne. Jeg kunne afprøve værktøjerne i praksis og vende tilbage med konkrete erfaringer. Det gjorde læringen langt mere holdbar.",
      author: "Mellemleder i produktionsvirksomhed",
      stars: 5
    },
    {
      quote: "Jeg blev en tydeligere leder uden at blive hårdere. Forløbet lærte mig, hvordan man kan skabe klare rammer og forventninger samtidig med, at man bevarer relationen til sine medarbejdere.",
      author: "Teamleder i servicebranchen",
      stars: 5
    },
    {
      quote: "Et af de få lederforløb, hvor der faktisk sker forandringer mellem kursusdagene. Kombinationen af refleksion, konkrete værktøjer og sparring med andre ledere gjorde en kæmpe forskel for mig.",
      author: "Ejerleder",
      stars: 5
    },
    {
      quote: "Da jeg startede forløbet, stod jeg et sted i mit arbejdsliv, hvor jeg havde mistet troen på mine egne evner og var i tvivl om næste skridt. Gennem arbejdet med mine værdier, mit ansvar og mit lederskab fik jeg genopbygget min selvtillid og fandt retningen igen. I dag står jeg i et nyt lederjob med langt større ro og klarhed.",
      author: "Jette, Souschef",
      stars: 5
    },
    {
      quote: "Jeg lærte, at lederskab ikke handler om at kontrollere alt. Det handler om at skabe tydelighed, tillid og ansvar. Det har gjort mig til en bedre leder og givet mig mere overskud i hverdagen.",
      author: "Deltager på årsforløbet",
      stars: 5
    },
    {
      quote: "Det var ikke endnu et kursus, hvor man bliver inspireret i en dag og glemmer det ugen efter. Forløbet over et år gav mig tid til at implementere, reflektere og udvikle mig mellem modulerne.",
      author: "Leder i en vækstvirksomhed",
      stars: 5
    }
  ];

  // Images representing the 4 modules (Lederrejsen)
  const moduleImages = [
    { url: images.leadershipSection, title: "Modul 1: Fundamentet" },
    { url: images.meetingEnvironment, title: "Modul 2: Kommunikation" },
    { url: images.sparringSection, title: "Modul 3: Ledelse i praksis" },
    { url: images.presentation, title: "Modul 4: Forankring" }
  ];

  const courseSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Course",
        "name": "Tag ansvar for dit lederskab – 12 måneders udviklingsforløb",
        "description": "Et unikt 12-måneders lederforløb med 4 moduler, der ruster ledere til at lede med ro, autoritet og stærke relationer.",
        "provider": {
          "@type": "Organization",
          "name": "Lederskab.nu",
          "url": "https://lederskab.nu"
        },
        "offers": {
          "@type": "Offer",
          "price": "29995",
          "priceCurrency": "DKK",
          "category": "Lederudvikling"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Lederforløb",
            "item": "https://lederskab.nu/kurser"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="12-Måneders Lederforløb | Tag ansvar for dit lederskab | Lederskab.nu"
        description="Struktureret 12-måneders udviklingsforløb for ledere og ejerledere med 4 moduler fordelt over året. Max. 10 deltagere pr. hold i hele Danmark."
        canonicalPath="/kurser"
        structuredData={courseSchema}
      />
      {/* Header Banner */}
      <section className="relative bg-gradient-to-br from-maersk-dark to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-left">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1.5 rounded-full">
              Lederakademiet
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 mb-6 leading-tight">
              {academyProgram.title}
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed mb-6">
              {academyProgram.subtitle} – et struktureret udviklingsforløb over 12 måneder, der ruster dig til at lede med ro, autoritet og menneskelig indsigt.
            </p>
            <a
              href="#kommende-hold"
              className="inline-flex items-center px-6 py-3.5 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold rounded-md transition-all shadow-md hover:shadow-lg text-sm"
            >
              <i className="fa-solid fa-calendar-days mr-2" /> Se kommende hold
            </a>
          </div>
        </div>
      </section>

      {/* Upcoming Hold Section */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <UpcomingCoursesSection showHeader={true} id="kommende-hold" />
        </div>
      </section>

      {/* Program Description */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 text-left space-y-6">
              <h2 className="font-display text-3xl font-bold text-maersk-dark">
                Mere end et kursus – en investering i dig selv
              </h2>
              <p className="text-navy-700 leading-relaxed text-base sm:text-lg">
                {academyProgram.description}
              </p>
              <div className="space-y-4 text-sm text-navy-700">
                <p className="flex items-center gap-3">
                  <i className="fa-solid fa-calendar-days text-brand-orange text-lg shrink-0" />
                  <span><strong>Varighed:</strong> {academyProgram.duration}</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="fa-solid fa-location-dot text-brand-orange text-lg shrink-0" />
                  <span><strong>Lokationer:</strong> {academyProgram.location}</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="fa-solid fa-users text-brand-orange text-lg shrink-0" />
                  <span><strong>Holdstørrelse:</strong> Små, eksklusive hold med ca. 10 ledere for optimal fortrolighed og sparring.</span>
                </p>
              </div>
            </div>

            {/* Right Pricing Card */}
            <div className="lg:col-span-5">
              <div className="bg-maersk-light rounded-3xl p-8 border border-maersk-blue/10 shadow-xl text-left space-y-6">
                <h3 className="font-display text-2xl font-bold text-maersk-dark">
                  Prisstruktur
                </h3>
                
                <div className="space-y-4">
                  {/* Complete Program */}
                  <div className="bg-white p-5 rounded-2xl border border-maersk-blue/10 shadow-xs relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-brand-orange text-white text-[9px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl">
                      Mest populær
                    </div>
                    <p className="text-xs font-bold text-gray-400 uppercase">Komplet 12-måneders lederforløb</p>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-3xl font-extrabold text-maersk-blue">{academyProgram.priceFullProgram}</span>
                      <span className="text-xs text-navy-700 font-medium">+ moms</span>
                    </div>
                    <p className="text-xs text-brand-orange font-semibold mt-1">Normalværdi: {academyProgram.normalValue} + moms</p>
                    <p className="text-xs text-navy-700 mt-3">Inkluderer alle 4 moduler, fuld forplejning, undervisning og materialer.</p>
                  </div>

                  {/* Single Day */}
                  <div className="bg-white/60 p-5 rounded-2xl border border-maersk-blue/5">
                    <p className="text-xs font-bold text-gray-400 uppercase">Enkelt kursusdag</p>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-2xl font-bold text-navy-800">{academyProgram.priceSingleDay}</span>
                      <span className="text-xs text-navy-700 font-medium">+ moms</span>
                    </div>
                    <p className="text-xs text-navy-700 mt-2">For dig, der ønsker at deltage på et specifikt modul.</p>
                  </div>
                </div>

                <Link
                  to="/kontakt"
                  className="w-full block bg-brand-orange hover:bg-brand-orange-hover text-white text-center py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Tilmeld dig nu <i className="fa-solid fa-arrow-right ml-2 text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Modules Breakdown */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-3 py-1 rounded-full">
              Programmets Opbygning
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark mt-3">
              De 4 moduler i dybden
            </h2>
            <p className="text-navy-700 mt-3">
              Hvert modul er nøje sammensat for at give dig en logisk, progressiv udvikling gennem året.
            </p>
          </div>

          <div className="space-y-8 max-w-5xl mx-auto">
            {academyProgram.modules.map((mod) => (
              <div
                key={mod.number}
                className="bg-white rounded-3xl border border-maersk-blue/10 shadow-md p-8 sm:p-10 text-left grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
              >
                {/* Left: Number & Title */}
                <div className="lg:col-span-4 space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-3 py-1 rounded-full">
                    Modul 0{mod.number}
                  </span>
                  <h3 className="font-display text-2xl font-bold text-maersk-dark pt-2">
                    {mod.title}
                  </h3>
                </div>

                {/* Right: Description & Bullets */}
                <div className="lg:col-span-8 space-y-4">
                  <p className="text-navy-700 text-sm sm:text-base leading-relaxed">
                    {mod.description}
                  </p>
                  <div className="border-t border-navy-50 pt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                      Centrale emner på modulet:
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-navy-700">
                      {mod.bullets.map((b, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <i className="fa-solid fa-circle-check text-brand-orange mt-1 text-xs shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lederrejsen Section (Visuals from modules) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-maersk-blue bg-maersk-light px-3 py-1 rounded-full">
              Lederrejsen i Billeder
            </span>
            <h2 className="font-display text-3xl font-bold text-maersk-dark mt-3">
              Glimt fra de forskellige moduler
            </h2>
            <p className="text-navy-700 mt-3">
              Vi vægter det personlige møde, den trygge atmosfære og den dybe sparring højt. Her er et indblik i vores professionelle rammer.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {moduleImages.map((img, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-white border border-maersk-light shadow-xs"
              >
                <div className="h-[300px] overflow-hidden">
                  <img
                    src={getOptimizedImageUrl(img.url, "width=400,quality=80,format=auto")}
                    alt={img.title}
                    width="400"
                    height="300"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 text-left bg-maersk-light border-t border-maersk-blue/5">
                  <h4 className="font-display font-bold text-maersk-dark text-sm">{img.title}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Participant Testimonials (Det siger tidligere deltagere) */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-3 py-1 rounded-full">
              Deltagerudtalelser
            </span>
            <h2 className="font-display text-3xl font-bold text-maersk-dark mt-3">
              Det siger tidligere deltagere
            </h2>
            <p className="text-navy-700 mt-3">
              Læs om den personlige og professionelle transformation, som vores deltagere har oplevet på årsforløbet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {courseTestimonials.slice(0, 3).map((t, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 border border-maersk-blue/10 shadow-md hover:shadow-lg transition-all duration-300 flex flex-col justify-between text-left"
              >
                <div>
                  <div className="flex gap-1 text-amber-400 mb-4">
                    {[...Array(t.stars)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-xs" />
                    ))}
                  </div>
                  <p className="text-navy-700 text-sm leading-relaxed italic mb-6">
                    "{t.quote}"
                  </p>
                </div>
                <div className="border-t border-navy-50 pt-4">
                  <h4 className="font-bold text-maersk-dark text-xs">{t.author}</h4>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/anbefalinger"
              className="inline-flex items-center gap-2 text-brand-orange hover:text-brand-orange-hover font-bold tracking-wide transition-colors duration-300 group"
            >
              <span>Læs flere udtalelser fra tidligere deltagere</span>
              <i className="fa-solid fa-arrow-right transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark">
            Klar til at tage næste skridt i din lederrejse?
          </h2>
          <p className="text-navy-700 text-lg max-w-2xl mx-auto">
            Book en uforpligtende afklaringssamtale, så vi sammen kan finde ud af, om årsforløbet er det rette match for dig.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
            >
              Book en afklarende samtale <i className="fa-solid fa-calendar-check ml-2" />
            </Link>
            <Link
              to="/virksomheder"
              className="bg-maersk-light hover:bg-maersk-blue hover:text-white text-maersk-blue px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300"
            >
              Se virksomhedsløsninger
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
